import { useEffect, useMemo, useState } from 'react';
import { useFormikContext } from 'formik';
import * as Yup from 'yup';
import {
  Button,
  CircularProgress,
  Divider,
  FormControl,
  FormControlLabel,
  FormHelperText,
  InputAdornment,
  InputLabel,
  ListItemIcon,
  ListItemText,
  MenuItem,
  Paper,
  Select,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import DeleteOutlined from '@mui/icons-material/DeleteOutlined';
import { ContentState, convertFromHTML, EditorState } from 'draft-js';
import { convertToHTML } from 'draft-convert';
import useOrganizations from '@/features/organizations/useOrganizations';
import {
  useAddOrganizationCategoryMutation,
  useDeleteOrganizationCategoryMutation,
} from '@/features/organizations/api/OrgCategoryApi';
import WysiwygEditor from '@/components/WysiwygEditor';
import InputText from '@/components/InputText';
import { useAppSelector } from '@/hooks';
import { Category, Content, Setting } from '@/types';

export const DetailsSchema = Yup.object().shape({
  title: Yup.string()
    .strict(true)
    .min(4, 'Muy Corto!')
    .max(50, 'Muy Largo!')
    .required('Es necesario tener un título de contenido'),
  previewDescription: Yup.string().min(4, 'Muy Corto!').max(100, 'Muy Largo!'),
  description: Yup.string().min(4, 'Muy Corto!'),
  price: Yup.string().strict(true).required('Debes añadir un precio base'),
  startingPrice: Yup.string(),
  category: Yup.string()
    .strict(true)
    .required('Este contenido necesita una categoría'),
  order: Yup.number().strict(true).required('Es necesario agregar un orden'),
  status: Yup.string()
    .strict(true)
    .required('Debes añadir un estado para este contenido'),
});

const Details = () => {
  const { currentOrganization } = useOrganizations();

  const { values, handleChange, errors, touched, handleBlur, setFieldValue } =
    useFormikContext<Content>();

  const Categories = useAppSelector((state) => state.categories);
  const data = useAppSelector(
    (state) => state.organizations[currentOrganization?.id ?? '']?.categories
  );

  const [editorState, setEditorState] = useState<EditorState>();

  const [addOrganizationCategory, organizationCategoryState] =
    useAddOrganizationCategoryMutation();
  const [deleteOrganizationCategory, deleteOrganizationCategoryState] =
    useDeleteOrganizationCategoryMutation();

  const MenuProps = {
    PaperProps: {
      style: {
        maxHeight: 48 * 4.5 + 8,
        width: 250,
      },
    },
  };

  const isNewContent = useMemo(() => !values?.id, [values?.id]);

  const categories = Object.values(Categories);

  const categoryByOrgIndustry = useMemo(() => {
    return categories.filter(
      (c) => (c as Category).industry === currentOrganization?.industry
    );
  }, [categories, currentOrganization?.industry]);

  const optionPrices = useMemo(() => {
    const opt = ((values?.settings as Setting[]) ?? []).map((s) => 
      s?.options?.map((o) => (o.type === 'option' ? o.price : null)).filter((price) => price !== null)
    );
    return opt;
  }, [values?.settings]);

  const areAllPricesZero = optionPrices.every((p) => {
    return p?.every((price) => price === 0);
  });
  console.log("~ areAllPricesZero:", areAllPricesZero)

  const onEditorStateChange = (e: EditorState) => {
    const html = convertToHTML(e.getCurrentContent());
    setFieldValue('description', html);
    setEditorState(e);
  };

  useEffect(() => {
    if (values?.description && !editorState) {
      const blocksFromHTML = convertFromHTML(values.description);
      const state = ContentState.createFromBlockArray(
        blocksFromHTML.contentBlocks,
        blocksFromHTML.entityMap
      );

      setEditorState(EditorState.createWithContent(state));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [values?.description, editorState]);

  return (
    <>
      <Stack gap={2}>
        <Paper
          elevation={2}
          sx={{
            padding: 2,
            borderRadius: 2,
          }}
        >
          <Stack spacing={2}>
            <InputText label="Nombre" name="title" required={true} />
            <InputText
              label="Descripción previa"
              name="previewDescription"
              rows={2}
            />
            <Stack>
              <InputLabel
                error={!!errors.description && !!touched.description}
                shrink
              >
                Descripción
              </InputLabel>
              <WysiwygEditor
                value={editorState ?? EditorState.createEmpty()}
                onChange={onEditorStateChange}
              />
            </Stack>
          </Stack>
        </Paper>
      </Stack>

      <Paper sx={{ padding: 2 }} elevation={2}>
        <Stack gap={2}>
          <Typography variant="h4">Configuraciones</Typography>

          <InputText label="Orden" name="order" type="number" required={true} />
      
            <Stack direction={"row"} spacing={3}>
              <InputText label="Precio" name="price" required={true} />
              {!areAllPricesZero && !isNewContent ? (
                <InputText 
                  label="Precio Inicial" 
                  name="startingPrice" 
                  required={true} 
                  helperText={'Se recomienda tener el mismo precio que la opción seleccionada'} 
                />
            ): null}
          </Stack>

          <Stack direction="row" gap={2} alignItems="center">
            <FormControl fullWidth>
              <InputLabel id="content-category">Categoría</InputLabel>
              <Select
                name="category"
                label="Categoría"
                labelId="content-category"
                value={values?.category ?? ''}
                onChange={handleChange}
                required={isNewContent}
                error={!!errors.category && !!touched.category}
                onBlur={handleBlur}
                MenuProps={MenuProps}
                renderValue={(categoryId) => {
                  const allCategories = [
                    ...categoryByOrgIndustry,
                    ...(data ?? []),
                  ];
                  const selectedCategory = allCategories.find(
                    (Cat) => Cat.id === categoryId
                  );
                  return selectedCategory?.title;
                }}
              >
                {categoryByOrgIndustry.map((c) => (
                  <MenuItem key={(c as Category).id} value={(c as Category).id}>
                    <ListItemText>{(c as Category).title}</ListItemText>
                  </MenuItem>
                ))}

                {data?.length ? <Divider>Mis Categorías</Divider> : null}

                {data?.length
                  ? (data ?? []).map((c: Category) => (
                      <MenuItem key={c.id} value={c.id} sx={{ flex: 1 }}>
                        <ListItemText>{c.title}</ListItemText>
                        <ListItemIcon
                          color={
                            deleteOrganizationCategoryState.isLoading
                              ? 'error'
                              : 'primary'
                          }
                          onClick={(e) => {
                            if (deleteOrganizationCategoryState.isLoading)
                              return;
                            e.stopPropagation();
                            deleteOrganizationCategory({
                              organizationId: currentOrganization?.id,
                              categoryId: c.id,
                            });
                          }}
                        >
                          <DeleteOutlined />
                        </ListItemIcon>
                      </MenuItem>
                    ))
                  : null}
              </Select>
              <FormHelperText error={!!errors.category && !!touched.category}>
                {!!errors.category && !!touched.category ? errors.category : ''}
              </FormHelperText>
            </FormControl>
            ó
            <TextField
              fullWidth
              placeholder="Agregar una nueva categoría"
              name="category"
              onChange={(e) => {
                e.stopPropagation();
                handleChange(e);
              }}
              onKeyUp={(e) => {
                if (e.key === 'Enter') {
                  addOrganizationCategory({
                    organizationId: currentOrganization.id,
                    payload: {
                      title: values.category,
                    },
                  });
                }
              }}
              slotProps={{
                input: {
                  endAdornment: (
                    <InputAdornment position="end">
                      {organizationCategoryState.isLoading ? (
                        <CircularProgress color="primary" size={24} />
                      ) : (
                        <Button
                          disabled={organizationCategoryState.isLoading}
                          variant="outlined"
                          onClick={() =>
                            addOrganizationCategory({
                              organizationId: currentOrganization.id,
                              payload: {
                                title: values.category,
                              },
                            })
                          }
                        >
                          Agregar
                        </Button>
                      )}
                    </InputAdornment>
                  ),
                },
              }}
            />
          </Stack>
        </Stack>
      </Paper>

      <Paper sx={{ padding: 2 }} elevation={2}>
        <Stack gap={2}>
          <Typography variant="h4">Estado</Typography>
          <Stack>
            <FormControlLabel
              label="Borrador"
              labelPlacement="start"
              sx={{
                margin: 0,
                display: 'flex',
                justifyContent: 'space-between',
              }}
              control={
                <Switch
                  name="draft"
                  value={'draft'}
                  checked={Boolean(values?.status === 'draft')}
                  onChange={(e) => {
                    handleChange(e);
                    if (e.target.checked) {
                      setFieldValue('status', 'draft');
                    }
                  }}
                />
              }
            />
            <FormControlLabel
              label="Publicado"
              labelPlacement="start"
              sx={{
                margin: 0,
                display: 'flex',
                justifyContent: 'space-between',
              }}
              control={
                <Switch
                  name="published"
                  value={'published'}
                  checked={Boolean(values?.status === 'published')}
                  onChange={(e) => {
                    handleChange(e);
                    if (e.target.checked) {
                      setFieldValue('status', 'published');
                    }
                  }}
                />
              }
            />
            <FormControlLabel
              label="Archivado"
              labelPlacement="start"
              sx={{
                margin: 0,
                display: 'flex',
                justifyContent: 'space-between',
              }}
              control={
                <Switch
                  name="archived"
                  value={'archived'}
                  checked={Boolean(values?.status === 'archived')}
                  onChange={(e) => {
                    handleChange(e);
                    if (e.target.checked) {
                      setFieldValue('status', 'archived');
                    }
                  }}
                />
              }
            />
          </Stack>
        </Stack>
      </Paper>
    </>
  );
};

export default Details;
